import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@tanstack/react-form';
import { toast } from 'sonner';
import { BookOpen, ChevronRight } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Dialog, DialogContent } from '@/shared/components/ui/Dialog';
import { useCreateSignal } from '@/shared/hooks/useSignals';
import { SignalOriginType, SignalVisibilityType } from '@momentum/types';
import { useAppForm } from '@/shared/components/form/form';
import { SectionDivider } from '@/shared/components/ui/SectionDivider';
import {
  MentionEditor,
  type MentionEditorRef,
} from '@/shared/components/mention-editor/MentionEditor';
import { SeverityField } from './create-signal/SeverityField';
import { RelevantPartiesField } from './create-signal/RelevantPartiesField';
import { VisibilityToggle } from './create-signal/VisibilityToggle';
import { AnonymousToggle } from './create-signal/AnonymousToggle';
import { VisibleToField } from './create-signal/VisibleToField';
import { ApplicabilityScopeField } from './create-signal/ApplicabilityScopeField';
import {
  createSignalSchema,
  defaultCreateSignalValues,
} from './create-signal/types/signal-form.types';

interface CreateSignalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateSignalDialog = ({
  open,
  onOpenChange,
}: CreateSignalDialogProps) => {
  const { t } = useTranslation('knowledge');
  const createSignalMutation = useCreateSignal();
  const editorRef = useRef<MentionEditorRef>(null);

  const form = useAppForm({
    defaultValues: defaultCreateSignalValues,
    validators: { onChange: createSignalSchema },
    onSubmit: async ({ value }) => {
      try {
        await createSignalMutation.mutateAsync({
          title: value.title.trim(),
          origin: { type: SignalOriginType.MANUAL },
          severity: value.severity,
          relevantParties: value.relevantParties,
          visibility:
            value.visibility === SignalVisibilityType.PRIVATE
              ? {
                  type: SignalVisibilityType.PRIVATE,
                  viewableBy: value.viewableBy,
                }
              : { type: SignalVisibilityType.PUBLIC },
          isAnonymous: value.isAnonymous,
          applicabilityScope: value.applicabilityScope,
        });
        toast.success(t('createSignalSuccess'));
        onOpenChange(false);
      } catch {
        toast.error(t('createSignalError'));
      }
    },
  });

  const visibility = useStore(form.store, (state) => state.values.visibility);
  const title = useStore(form.store, (state) => state.values.title);

  useEffect(() => {
    if (!open) {
      form.reset();
      editorRef.current?.clear();
    }
  }, [open, form]);

  useEffect(() => {
    if (visibility === SignalVisibilityType.PUBLIC) {
      form.setFieldValue('viewableBy', { users: [], teams: [] });
    }
  }, [visibility, form]);

  const handleMention = useCallback(
    (type: 'user' | 'team', id: string, _name: string) => {
      const prev = form.getFieldValue('relevantParties');

      if (type === 'team') {
        if (prev.teams.includes(id)) return;

        form.setFieldValue('relevantParties', {
          ...prev,
          teams: [...prev.teams, id],
        });

        return;
      }

      if (prev.users.includes(id)) return;

      form.setFieldValue('relevantParties', {
        ...prev,
        users: [...prev.users, id],
      });
    },
    [form]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="md"
        showCloseButton
        dismissibleByOutsideClick={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        overlayClassName="bg-black/60"
        className="bg-background border-white/20 shadow-2xl"
      >
        {/* Top bar — breadcrumb */}
        <div className="flex items-center gap-2 pb-4 border-b-2 border-border">
          <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
            <BookOpen className="w-3.5 h-3.5" />
            {t('createSignalBreadcrumb')}
            <ChevronRight className="w-2.5 h-2.5 rtl:rotate-180" />
            <span className="text-foreground/70">
              {t('createSignalBreadcrumbNew')}
            </span>
          </div>
        </div>

        {/* Signal text area with @ mention support */}
        <div className="mt-1">
          <form.AppField
            name="title"
            children={(field) => (
              <MentionEditor
                ref={editorRef}
                placeholder={t('createSignalPlaceholder')}
                onTextChange={(text) => field.handleChange(text)}
                onMention={handleMention}
                teamsLabel={t('pickerTeamsGroup')}
                peopleLabel={t('pickerPeopleGroup')}
              />
            )}
          />
        </div>

        {/* Properties section */}
        <SectionDivider label={t('propertiesSection')} />

        <div className="flex flex-col">
          <form.AppField
            name="severity"
            children={(field) => (
              <SeverityField
                value={field.state.value}
                onChange={(severity) => field.handleChange(severity)}
              />
            )}
          />
          <form.AppField
            name="relevantParties"
            children={(field) => (
              <RelevantPartiesField
                value={field.state.value}
                onChange={(parties) => field.handleChange(parties)}
              />
            )}
          />
        </div>

        {/* Visibility section */}
        <SectionDivider label={t('visibilitySection')} />

        <div className="flex flex-col">
          <form.AppField
            name="visibility"
            children={(field) => (
              <VisibilityToggle
                value={field.state.value}
                onChange={(visibility) => field.handleChange(visibility)}
              />
            )}
          />
          <form.AppField
            name="isAnonymous"
            children={(field) => (
              <AnonymousToggle
                value={field.state.value}
                onChange={(isAnonymous) => field.handleChange(isAnonymous)}
              />
            )}
          />

          {/* Visible To — conditional on Private */}
          <div
            className={`overflow-hidden transition-all duration-250 ease-in-out ${
              visibility === SignalVisibilityType.PRIVATE
                ? 'max-h-[70px] min-h-[40px] opacity-100'
                : 'max-h-0 min-h-0 opacity-0'
            }`}
          >
            <form.AppField
              name="viewableBy"
              children={(field) => (
                <VisibleToField
                  value={field.state.value}
                  onChange={(viewableBy) => field.handleChange(viewableBy)}
                />
              )}
            />
          </div>
        </div>

        {/* Applicability section — managers only */}
        <form.AppField
          name="applicabilityScope"
          children={(field) => (
            <ApplicabilityScopeField
              value={field.state.value}
              onChange={(v) => field.handleChange(v)}
            />
          )}
        />

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 mt-5">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createSignalMutation.isPending}
          >
            {t('createSignalCancel')}
          </Button>
          <Button
            onClick={() => form.handleSubmit()}
            disabled={createSignalMutation.isPending || !title.trim()}
            className="bg-[#7c6bc4] hover:bg-[#6a5aae] h-8 px-3 text-[13px]"
          >
            {createSignalMutation.isPending
              ? t('createSignalCreating')
              : t('createSignalCreate')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
